// SEO-EXPANSION v4: additive, no behavior change to existing pages.
import { CITY_BAZAAR, CITY_NAME, type IntentNode } from "./intent-registry";
import { hashSeed, pick } from "./title-tails";

const VERBS = ["trading","quoted","settling","moving","holding","printing"];
const CONTEXT = [
  "global bullion sentiment","dollar index moves","central bank purchases",
  "festival season demand","wedding season buying","import duty changes",
];
const TIPS = [
  "Always compare wastage and making charges before locking a quote.",
  "Ask for the day's printed rate slip when transacting at a jeweller.",
  "Hallmarked pieces protect resale value far better than unmarked stock.",
  "Buying smaller denominations stays more liquid than a single large bar.",
  "Check the spot rate against two sources before committing to a deal.",
];

export function generateIntentCopy(node: IntentNode): {
  intro: string;
  sections: Array<{ heading: string; body: string }>;
  faqs: Array<{ q: string; a: string }>;
} {
  const seed = hashSeed(node.slug);
  const cityName = node.city ? CITY_NAME[node.city] ?? node.city : node.region.toUpperCase();
  const bazaar = node.city ? CITY_BAZAAR[node.city] ?? "the local bullion market" : "global bullion markets";
  const metal = node.metal;
  const karat = node.karat ? node.karat.toUpperCase() : "24K";
  const unit = node.unit;
  const purity = karat === "24K" ? "1.000" : karat === "22K" ? "0.9166" : karat === "21K" ? "0.875" : "0.750";

  const intro =
    `Live ${karat} ${metal} ${node.intentCluster === "converter" ? "conversion" : "pricing"} for ${cityName} is ${pick(VERBS, seed)} against ${pick(CONTEXT, seed, 1)}. ` +
    `Buyers tracking ${unit}-based quotes in ${bazaar} use this page to verify the current ${node.currency} rate before walking into a shop, ` +
    `placing a digital order, or settling a long-running ${metal}-savings plan. We refresh the spot value every minute and recompute every karat row server-side, ` +
    `so the number on screen is the number a reputable jeweller in ${cityName} should match within a thin spread. ${pick(TIPS, seed, 2)}`;

  const sections = [
    {
      heading: `How ${karat} ${metal} per ${unit} is calculated`,
      body:
        `The base feed is the international spot ${metal} price in USD per troy ounce. We divide by 31.1035 to get USD per gram, ` +
        `multiply by the karat purity factor (${purity}), convert into ${node.currency} using the current mid-market FX rate, then scale to the requested ${unit}. ` +
        `1 tola equals 11.6638 grams, 1 ounce equals 31.1035 grams, and one sovereign is fixed at 8 grams across South Asian markets. ` +
        `Because every step is transparent, you can audit the conversion yourself instead of trusting a single retailer's chalkboard. ` +
        `This matters most for high-value transactions where even a one percent hidden spread compounds into a meaningful loss across a single ${unit}.`,
    },
    {
      heading: `Market context for ${cityName} buyers`,
      body:
        `${cityName} traders react quickly to ${pick(CONTEXT, seed, 3)} and overnight moves in London and New York. ` +
        `Liquidity tends to concentrate around ${bazaar} during morning hours, with retail spreads tightening once the day's reference rate is published. ` +
        `If you are buying ${metal} for long-term holding, prioritise hallmarked ${karat} stock; for jewellery, the same rate applies but additional making charges and wastage are negotiated separately. ` +
        `Watching the seven-day trend before a major purchase typically saves more than chasing minute-to-minute ticks. ` +
        `Seasonal cycles also matter — premiums widen ahead of weddings and major festivals, then normalise within a couple of weeks.`,
    },
    {
      heading: `Practical buying tips`,
      body:
        `${pick(TIPS, seed, 4)} ${pick(TIPS, seed, 5)} ${pick(TIPS, seed, 6)} ` +
        `When you receive a quote, ask the jeweller to break out the spot rate, the making charge, the wastage percentage, and any tax line item separately. ` +
        `This forces an apples-to-apples comparison across shops in ${cityName} and protects you from blended quotes that hide a premium inside the metal rate itself. ` +
        `For investment buyers, coin and small-bar formats almost always resell closer to spot than ornate jewellery, because the buyer does not pay you for someone else's craftsmanship. ` +
        `Keep the original invoice, hallmark certificate, and purity guarantee together — resale buyers across ${cityName} will discount unverified pieces sharply.`,
    },
    {
      heading: `Regional ${metal} nuance`,
      body:
        `In ${cityName}, the convention is to quote ${metal} in ${unit === "tola" ? "tola for retail and grams for bullion" : `${unit}s with side-quotes in tola`}. ` +
        `Festival cycles, wedding seasons, and budget announcements move local premiums above or below the international parity price. ` +
        `Tracking the ${node.currency} per ${unit} number on this page next to the underlying USD/oz spot lets you see whether you are paying a fair regional premium or an inflated retail markup. ` +
        `Local taxes vary by state and province — always confirm the final printed bill matches the calculation here plus the declared statutory charges. ` +
        `Cross-border travellers should also check customs allowances; carrying ${metal} above the personal limit triggers duty even on hallmarked pieces.`,
    },
  ];

  const faqs = [
    { q: `What is today's ${karat} ${metal} rate in ${cityName} per ${unit}?`, a: `The live rate is displayed at the top of this page in ${node.currency} and refreshes every minute from international spot data combined with mid-market FX.` },
    { q: `Why does the rate differ slightly between jewellers in ${cityName}?`, a: `Each shop adds its own spread, making charges, and wastage on top of the spot benchmark. The number here is the underlying metal value before those retail additions.` },
    { q: `Is ${karat} ${metal} a good buy for long-term holding?`, a: `Higher-karat metal preserves more intrinsic value but trades at a small premium for purity. Holding strategy should match your liquidity needs and resale market.` },
    { q: `How accurate is the conversion math used here?`, a: `Every formula is shown in the sections above; reproduce it in any calculator and you will land within rounding distance of the displayed price.` },
  ];

  return { intro, sections, faqs };
}